import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import Panel from "@ui/panel/Panel";
import { ICellFactoryResult } from "@ui/table/Table";
import Signal from "badmfck-signal";
import { Check, ChevronRight, Search } from "lucide-react";
import { useEffect, useState } from "react";
import styled from "styled-components";

type TOrder<T> = keyof T | "__itemType" | "__actions";
interface TItemPopupProps<T> {
	onSelected: (items?: T[]) => void;
	onClose: () => void;
	items: T[];
	multi: boolean;
	title: string;
	searchable: boolean;
	selectedItems?: T[];
	comparator?: (a: T, b: T) => boolean;
	maxSelected?: number;
	manageSelection?: boolean;
	order?: TOrder<T>[];
	gridColumns?: string;
	itemType?: string;
	cellFactory?: (
		row: T, cell: any, index: number, key?: number, name?: string,
		sx?: React.CSSProperties, expanded?: boolean, rowID?: string,
	) => ICellFactoryResult;
}

const Content = styled.div`
	display: flex; flex-direction: column; min-height: 0; color: #344054; font-size: 14px;
	button, input { font: inherit; }
	button:focus-visible, input:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	.toolbar { display: flex; align-items: center; gap: 12px; padding: 14px 20px; flex-shrink: 0; border-bottom: 1px solid #e1e6ef; }
	.search { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; color: #98a2b3; }
	.search input { width: 100%; min-width: 0; height: 32px; padding: 4px; border: 0; background: transparent; color: #344054; }
	.search input::placeholder { color: #98a2b3; }
	.filter { padding: 6px 10px; border: 1px solid #e1e6ef; border-radius: 6px; background: #fff; color: #667085; font-size: 12px; white-space: nowrap; cursor: pointer; }
	.filter[aria-pressed="true"] { background: #eef2ff; border-color: #c7d2fe; color: #4659a8; }
	.list { overflow-y: auto; min-height: 0; max-height: 56vh; scrollbar-gutter: stable; }
	.empty { padding: 32px 20px; text-align: center; color: #667085; font-size: 13px; }
	.footer { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1px solid #e1e6ef; flex-shrink: 0; }
	.count { flex: 1; min-width: 0; font-size: 12px; color: #667085; line-height: 1.5; }
	.count strong { color: #344054; font-weight: 600; }
	.action { min-height: 36px; padding: 7px 16px; border: 1px solid #cfd6e0; border-radius: 6px; background: #fff; color: #344054; cursor: pointer; font-size: 13px; font-weight: 500; }
	.action:hover { background: #f5f7fa; }
	.primary { background: #344054; border-color: #344054; color: #fff; }
	.primary:hover { background: #253247; }
	@media (max-width: 480px) { .toolbar, .footer { padding: 12px 16px; } .footer { flex-wrap: wrap; } }
`;
const Row = styled.button`
	display: flex; align-items: center; gap: 14px; width: 100%; min-height: 58px;
	padding: 10px 20px; box-sizing: border-box; border: 0; border-bottom: 1px solid #edf0f5;
	background: #fff; color: #344054; text-align: left; cursor: pointer; transition: background .15s;
	&:last-child { border-bottom: 0; }
	&:hover { background: #f8fafc; }
	&[data-selected="true"] { background: #f5f7fd; }
	&[data-selected="true"]:hover { background: #eef2fb; }
	.subject { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; }
	.title { font-size: 14px; font-weight: 600; line-height: 1.4; overflow-wrap: anywhere; }
	.description { color: #667085; font-size: 13px; line-height: 1.45; overflow-wrap: anywhere; }
	.code { font-variant-numeric: tabular-nums; }
	.flag { width: 24px; height: 17px; border-radius: 2px; object-fit: cover; box-shadow: 0 0 0 1px #10182814; }
	.check { display: flex; align-items: center; justify-content: center; width: 18px; height: 18px; flex: 0 0 18px; border: 1px solid #cfd6e0; border-radius: 5px; color: transparent; background: #fff; }
	&[data-selected="true"] .check { color: #fff; background: #5265a5; border-color: #5265a5; }
	.open { color: #98a2b3; flex-shrink: 0; }
	@media (max-width: 480px) { padding: 10px 16px; }
`;

const itemLabel = (item: any, index: number) => {
	const value = item.alpha2 ?? item.code ?? item.value ?? item.id;
	const name = item.name ?? item.title ?? item.label ?? Object.entries(item).find(
		([key, value]) => !["id", "code", "value", "alpha2", "description", "selected", "string"].includes(key)
			&& !key.startsWith("$__") && typeof value === "string",
	)?.[1];
	const hasValue = value !== undefined && value !== null && value !== "";
	return {
		title: String(hasValue && !item.alpha2 ? value : name ?? item.description ?? msg("templateItem_2422ddf2", { v0: index + 1 })),
		description: String(hasValue && !item.alpha2 ? [name, item.description].filter(Boolean).join(" · ") : item.description && name ? item.description : ""),
		code: hasValue && !item.alpha2,
		flag: item.alpha2,
	};
};

export const S_SELECT_ITEMS_POPUP = new Signal<TItemPopupProps<any> | null>("S_SELECT_ITEMS_POPUP");

export const SelectItemsPopup = () => {
	useLocale();
	const request = S_SELECT_ITEMS_POPUP.use();
	const [items, setItems] = useState<any[]>([]);
	const [search, setSearch] = useState("");
	const [selectedOnly, setSelectedOnly] = useState(false);

	useEffect(() => {
		if (!request) return;
		setItems(request.items.map(item => {
			const selected = request.selectedItems?.some(current =>
				request.comparator ? request.comparator(item, current) : item === current,
			) ?? false;
			return typeof item !== "object" || item === null
				? { name: item, selected, string: true }
				: { ...item, selected };
		}));
		setSearch("");
		setSelectedOnly(false);
	}, [request]);

	if (!request) return null;

	const selected = items.filter(item => item.selected).length;
	const visibleItems = items.map((item, index) => ({ item, index, ...itemLabel(item, index) }))
		.filter(row => (!selectedOnly || row.item.selected)
			&& [row.title, row.description].join(" ").toLowerCase().includes(search.trim().toLowerCase()));
	const closePopup = () => {
		request.onClose();
		S_SELECT_ITEMS_POPUP.invoke(null);
	};
	const selectItem = (item: any) => {
		if (!request.multi) {
			request.onSelected([item]);
			closePopup();
			return;
		}
		if (!item.selected && request.maxSelected && selected >= request.maxSelected) return;
		setItems(items.map(current => current === item ? { ...current, selected: !current.selected } : current));
	};

	return (
		<Panel growContent disableScroll decorator="absolute" onClose={closePopup}
			label={<span style={{ fontSize: 18, fontWeight: 600, color: "#344054", lineHeight: 1.4 }}>{request.title}</span>}
			contentPadding="0"
			sx={{ width: "760px", maxWidth: "94vw", maxHeight: "90dvh", borderRadius: "12px", overflow: "hidden", background: "#fff" }}>
			<Content>
				{(request.searchable || items.length > 15 || request.multi) && <div className="toolbar">
					<label className="search">
						<Search size={17} aria-hidden="true" />
						<input type="search" placeholder={msg("searchByCodeOrName")} aria-label={msg("searchByCodeOrName")}
							value={search} onChange={event => setSearch(event.target.value)} />
					</label>
					{request.multi && <button type="button" className="filter" aria-pressed={selectedOnly}
						onClick={() => setSelectedOnly(!selectedOnly)}>{msg("selected_51505f78")}{selected}</button>}
				</div>}
				<div className="list" aria-label={request.title}>
					{visibleItems.map(({ item, index, title, description, code, flag }) => <Row key={index} type="button"
						data-selected={item.selected} aria-pressed={request.multi ? item.selected : undefined}
						onClick={() => selectItem(item)}>
						{flag && <img className="flag" src={`https://flagcdn.com/${String(flag).toLowerCase()}.svg`} alt="" />}
						<span className="subject">
							<span className={code ? "title code" : "title"}>{title}</span>
							{description && <span className="description">{description}</span>}
						</span>
						{request.multi ? <span className="check" aria-hidden="true"><Check size={13} strokeWidth={2.5} /></span>
							: <ChevronRight className="open" size={16} aria-hidden="true" />}
					</Row>)}
					{visibleItems.length === 0 && <div className="empty">{selectedOnly && selected === 0 ? msg("noItemsSelected") : msg("noMatchesFound")}</div>}
				</div>
				<div className="footer">
					<span className="count">{request.multi && <><strong>{selected}</strong>{msg("of")}{items.length}{msg("selected")}{request.maxSelected ? msg("templateLimit_4a6827fa", { v0: request.maxSelected }) : ""}</>}</span>
					<button type="button" className="action" onClick={closePopup}>{request.multi ? msg("cancel") : msg("close")}</button>
					{request.multi && <button type="button" className="action primary" onClick={() => {
						request.onSelected(items.filter(item => item.selected));
						closePopup();
					}}>{request.manageSelection ? msg("apply") : msg("addSelected")}</button>}
				</div>
			</Content>
		</Panel>
	);
};
